import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import type { ServitutPaategningFiling } from "src/dataCollector/parseDokumentRevisionssporXml";

const TINGBOG_DOKUMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function pdfBytesBlob(bytes: Uint8Array): Blob {
  return new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
}

/** Land-registry document UUID. Akt file names such as 51_M_205 are ignored. */
export function firstTingbogDokumentUuid(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = `${value || ""}`.trim();
    if (TINGBOG_DOKUMENT_UUID.test(trimmed)) {
      return trimmed;
    }
  }
  return "";
}

export function laterPaategningFilings(filings: ServitutPaategningFiling[]): ServitutPaategningFiling[] {
  return filings.filter((filing) => filing.kind === "paategning" && Boolean(filing.id));
}

export type BundleDocumentBilag = {
  ref: string;
  gml?: boolean;
};

export type BundleAppendixStep =
  | { kind: "bilag"; ref: string; gml: boolean }
  | { kind: "paategning"; filingId: string };

function bilagKey(ref: string): string {
  return ref.trim().toLowerCase();
}

/**
 * Servitut bilag, then each påtegning followed by the bilag filed with it.
 * A newer servitut lists later bilag on the current document; those still follow their påtegning.
 * Matrikulære ændringer stay out.
 */
export function bundleAppendixSteps(
  filings: ServitutPaategningFiling[],
  documentBilag: BundleDocumentBilag[] = [],
  options?: { includeBilag?: boolean },
): BundleAppendixStep[] {
  const includeBilag = Boolean(options?.includeBilag);
  const gml = new Map<string, boolean>();
  for (const bilag of documentBilag) {
    const key = bilagKey(bilag.ref);
    if (key) {
      gml.set(key, Boolean(bilag.gml));
    }
  }
  const onPaategning = new Set<string>();
  const onMatrikulaer = new Set<string>();
  const onAndet = new Set<string>();
  for (const filing of filings) {
    const claimed = filing.kind === "paategning"
      ? onPaategning
      : filing.kind === "matrikulaerAendring"
        ? onMatrikulaer
        : filing.kind === "andet"
          ? onAndet
          : null;
    if (!claimed) {
      continue;
    }
    for (const ref of filing.bilagRefs) {
      const key = bilagKey(ref);
      if (key) {
        claimed.add(key);
      }
    }
  }

  const steps: BundleAppendixStep[] = [];
  const seen = new Set<string>();
  const pushBilag = (ref: string) => {
    const trimmed = ref.trim();
    const key = bilagKey(trimmed);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    steps.push({ kind: "bilag", ref: trimmed, gml: gml.get(key) ?? false });
  };

  if (includeBilag) {
    for (const bilag of documentBilag) {
      const key = bilagKey(bilag.ref);
      if (onPaategning.has(key) || onMatrikulaer.has(key) || onAndet.has(key)) {
        continue;
      }
      pushBilag(bilag.ref);
    }
    for (const filing of filings) {
      if (filing.kind !== "servitut") {
        continue;
      }
      for (const ref of filing.bilagRefs) {
        if (onPaategning.has(bilagKey(ref))) {
          continue;
        }
        pushBilag(ref);
      }
    }
  }

  for (const filing of filings) {
    if (filing.kind === "paategning" && filing.id.trim()) {
      steps.push({ kind: "paategning", filingId: filing.id.trim() });
      if (includeBilag) {
        for (const ref of filing.bilagRefs) {
          pushBilag(ref);
        }
      }
      continue;
    }
    if (includeBilag && filing.kind === "andet") {
      for (const ref of filing.bilagRefs) {
        pushBilag(ref);
      }
    }
  }
  return steps;
}

async function copyPdfPart(output: PDFDocument, part: Blob): Promise<boolean> {
  try {
    const source = await PDFDocument.load(await part.arrayBuffer(), { ignoreEncryption: true });
    const pages = await output.copyPages(source, source.getPageIndices());
    if (!pages.length) {
      return false;
    }
    pages.forEach((page) => output.addPage(page));
    return true;
  } catch (error) {
    console.warn("[bundle] copying PDF failed, drawing its pages", error);
    return false;
  }
}

/** pdf.js can open Tingbog files whose page tree pdf-lib cannot copy. */
async function drawPdfPart(output: PDFDocument, part: Blob): Promise<boolean> {
  if (typeof document === "undefined") {
    return false;
  }
  try {
    const [{ pdfjs }, worker, { renderPdfPageToImage }] = await Promise.all([
      import("react-pdf"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
      import("src/modules/ANNModule/ANNPDFPageToImage"),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    const pdf = await pdfjs.getDocument({ data: await part.arrayBuffer() }).promise;
    const pageCount = pdf.numPages;
    await pdf.destroy();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      const png = await renderPdfPageToImage(part, pageNumber, 1.5);
      const image = await output.embedPng(new Uint8Array(await png.arrayBuffer()));
      const page = output.addPage([image.width, image.height]);
      page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
    }
    return pageCount > 0;
  } catch (error) {
    console.warn("[bundle] drawing PDF pages failed", error);
    return false;
  }
}

export async function mergePdfBlobs(parts: Blob[]): Promise<Blob> {
  const output = await PDFDocument.create();
  for (const part of parts) {
    if (!part || part.size < 8) {
      continue;
    }
    if (await copyPdfPart(output, part)) {
      continue;
    }
    if (!(await drawPdfPart(output, part))) {
      console.warn("[bundle] skipped a PDF that could not be copied");
    }
  }
  return pdfBytesBlob(await output.save());
}

export function bundleStartPages(tocPageCount: number, documentPageCounts: number[]): Array<number | null> {
  let cursor = tocPageCount + 1;
  return documentPageCounts.map((count) => {
    if (count < 1) {
      return null;
    }
    const start = cursor;
    cursor += count;
    return start;
  });
}

export type BundleSection = {
  title: string;
  priority?: string;
  pdf: Blob | null;
};

export type BundleLabels = {
  contents: string;
  notFound: string;
  page: string;
  priority?: string;
  servitut?: string;
  general?: string;
  bfeLabel?: string;
  bfeValue?: string;
  addressLabel?: string;
  addressValue?: string;
  countLabel?: string;
  countValue?: string;
  pagesLabel?: string;
};

const A4: [number, number] = [595.28, 841.89];
const TOC_MARGIN = 48;
const TOC_FONT = 11;
const TOC_TITLE = 16;
const TOC_LINE = 16;
const TOC_LOGO_HEIGHT = 32;
const TOC_LOGO_URL = "/Moelbaklogo-Groen-Stort.png";

let moelbakLogoBytes: Promise<Uint8Array | null> | null = null;

async function loadMoelbakLogoBytes(): Promise<Uint8Array | null> {
  if (!moelbakLogoBytes) {
    moelbakLogoBytes = (async () => {
      try {
        const origin = typeof window !== "undefined" ? window.location?.origin : "";
        const response = await fetch(origin ? `${origin}${TOC_LOGO_URL}` : TOC_LOGO_URL);
        if (!response.ok) {
          return null;
        }
        return new Uint8Array(await response.arrayBuffer());
      } catch {
        return null;
      }
    })();
  }
  return moelbakLogoBytes;
}

function winAnsi(text: string): string {
  return text.replace(/[^\u0000-\u00FF]/g, "?");
}

export async function pdfTitlePage(title: string): Promise<Blob> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(A4);
  const font = await doc.embedFont(StandardFonts.HelveticaBold);
  const text = winAnsi(title.trim() || "Bilag");
  const size = 18;
  const width = font.widthOfTextAtSize(text, size);
  page.drawText(text, {
    x: Math.max(TOC_MARGIN, (A4[0] - width) / 2),
    y: A4[1] / 2,
    size,
    font,
    color: rgb(0, 0, 0),
  });
  return pdfBytesBlob(await doc.save());
}

async function pageCountOf(blob: Blob | null): Promise<number> {
  if (!blob || blob.size < 8) {
    return 0;
  }
  try {
    const doc = await PDFDocument.load(await blob.arrayBuffer(), { ignoreEncryption: true });
    return doc.getPageCount();
  } catch {
    return 0;
  }
}

const GREEN = rgb(0.12, 0.29, 0.24);
const INK = rgb(0.16, 0.18, 0.17);
const HAIRLINE = rgb(0.78, 0.81, 0.79);
const MISSING = rgb(0.62, 0.28, 0.24);

function fitText(text: string, font: PDFFont, size: number, width: number): string {
  const value = winAnsi(text.trim() || "-");
  if (font.widthOfTextAtSize(value, size) <= width) {
    return value;
  }
  let kept = value;
  while (kept.length > 1 && font.widthOfTextAtSize(`${kept}...`, size) > width) {
    kept = kept.slice(0, -1);
  }
  return `${kept}...`;
}

function drawRule(page: PDFPage, y: number, color = HAIRLINE, thickness = 0.6) {
  page.drawLine({
    start: { x: TOC_MARGIN, y },
    end: { x: A4[0] - TOC_MARGIN, y },
    thickness,
    color,
  });
}

export async function pdfFigurePage(title: string, body: string, imagePng?: Uint8Array | null): Promise<Blob> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(A4);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  let y = A4[1] - TOC_MARGIN;
  page.drawText(fitText(title, bold, 16, A4[0] - TOC_MARGIN * 2), {
    x: TOC_MARGIN,
    y: y - 16,
    size: 16,
    font: bold,
    color: GREEN,
  });
  y -= 28;
  drawRule(page, y, GREEN, 1);
  y -= 22;
  const note = winAnsi(body);
  const words = note.split(/\s+/);
  let line = "";
  const maxWidth = A4[0] - TOC_MARGIN * 2;
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(next, 11) > maxWidth && line) {
      page.drawText(line, { x: TOC_MARGIN, y, size: 11, font, color: INK });
      y -= 15;
      line = word;
    } else {
      line = next;
    }
  }
  if (line) {
    page.drawText(line, { x: TOC_MARGIN, y, size: 11, font, color: INK });
    y -= 22;
  }
  if (imagePng && imagePng.length > 8) {
    try {
      const isJpeg = imagePng[0] === 0xff && imagePng[1] === 0xd8 && imagePng[2] === 0xff;
      const image = isJpeg ? await doc.embedJpg(imagePng) : await doc.embedPng(imagePng);
      const maxImageWidth = A4[0] - TOC_MARGIN * 2;
      const maxImageHeight = y - TOC_MARGIN;
      const scale = Math.min(maxImageWidth / image.width, maxImageHeight / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      page.drawImage(image, {
        x: TOC_MARGIN + (maxImageWidth - width) / 2,
        y: y - height,
        width,
        height,
      });
    } catch {
      // The note still explains where the GML file can be found.
    }
  }
  return pdfBytesBlob(await doc.save());
}

async function renderContents(
  sections: Array<{ title: string; priority?: string; startPage: number | null; pageCount: number }>,
  labels: BundleLabels,
): Promise<PDFDocument> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  let page = doc.addPage(A4);
  let y = A4[1] - TOC_MARGIN;
  const logoBytes = await loadMoelbakLogoBytes();
  let logoHeight = 0;
  if (logoBytes) {
    try {
      const logo = await doc.embedPng(logoBytes);
      const scale = Math.min(TOC_LOGO_HEIGHT / logo.height, (A4[0] / 2 - TOC_MARGIN) / logo.width);
      const logoWidth = logo.width * scale;
      logoHeight = logo.height * scale;
      page.drawImage(logo, {
        x: A4[0] - TOC_MARGIN - logoWidth,
        y: y - logoHeight,
        width: logoWidth,
        height: logoHeight,
      });
    } catch {
      logoHeight = 0;
    }
  }
  page.drawText(fitText(labels.contents, bold, 18, A4[0] / 2 - TOC_MARGIN), {
    x: TOC_MARGIN,
    y: y - 18,
    size: 18,
    font: bold,
    color: GREEN,
  });
  y -= Math.max(28, logoHeight + 10);
  drawRule(page, y, GREEN, 1.2);

  const found = sections.filter((section) => section.startPage != null && section.pageCount > 0);
  const firstPage = found.length ? Math.min(...found.map((section) => section.startPage as number)) : null;
  const lastPage = found.length
    ? Math.max(...found.map((section) => (section.startPage as number) + section.pageCount - 1))
    : null;
  const pageSpan = firstPage == null || lastPage == null ? "-" : firstPage === lastPage ? `${firstPage}` : `${firstPage} - ${lastPage}`;

  if (labels.general) {
    y -= 20;
    const facts = [
      [labels.bfeLabel, labels.bfeValue],
      ...(labels.addressValue ? [[labels.addressLabel, labels.addressValue]] : []),
      [labels.countLabel, labels.countValue],
      [labels.pagesLabel, pageSpan],
    ]
      .filter((row): row is [string, string] => Boolean(row[0]))
      .map(([label, value]) => winAnsi(`${label} ${value || "-"}`));
    const maxWidth = A4[0] - TOC_MARGIN * 2;
    const dot = "  \u00B7  ";
    let line = "";
    const flush = () => {
      if (!line) {
        return;
      }
      page.drawText(line, { x: TOC_MARGIN, y, size: 10, font, color: INK });
      y -= 14;
      line = "";
    };
    for (const fact of facts) {
      const next = line ? `${line}${dot}${fact}` : fact;
      if (line && font.widthOfTextAtSize(next, 10) > maxWidth) {
        flush();
        line = fact;
      } else {
        line = next;
      }
    }
    flush();
    y -= 8;
  } else {
    y -= 22;
  }

  const priorityWidth = 52;
  const pageWidth = 78;
  const titleX = TOC_MARGIN + priorityWidth;
  const titleWidth = A4[0] - TOC_MARGIN - pageWidth - titleX;

  const drawHeader = () => {
    page.drawText(winAnsi(labels.priority || ""), { x: TOC_MARGIN, y, size: 9, font: bold, color: GREEN });
    page.drawText(winAnsi(labels.servitut || labels.contents), { x: titleX, y, size: 9, font: bold, color: GREEN });
    const pageHeader = winAnsi(labels.page);
    page.drawText(pageHeader, {
      x: A4[0] - TOC_MARGIN - font.widthOfTextAtSize(pageHeader, 9),
      y,
      size: 9,
      font: bold,
      color: GREEN,
    });
    y -= 6;
    drawRule(page, y, GREEN, 0.8);
    y -= 16;
  };
  drawHeader();

  for (const section of sections) {
    if (y < TOC_MARGIN + 8) {
      page = doc.addPage(A4);
      y = A4[1] - TOC_MARGIN;
      drawHeader();
    }
    const startPage = section.startPage;
    const missing = startPage == null;
    const rowColor = missing ? MISSING : INK;
    const where = startPage == null
      ? labels.notFound
      : section.pageCount > 1
        ? `${startPage} - ${startPage + section.pageCount - 1}`
        : `${startPage}`;
    page.drawText(fitText(section.priority || "-", font, TOC_FONT, priorityWidth - 8), {
      x: TOC_MARGIN,
      y,
      size: TOC_FONT,
      font,
      color: rowColor,
    });
    page.drawText(fitText(section.title || "-", font, TOC_FONT, titleWidth), {
      x: titleX,
      y,
      size: TOC_FONT,
      font,
      color: rowColor,
    });
    const marker = fitText(where, font, TOC_FONT, pageWidth);
    page.drawText(marker, {
      x: A4[0] - TOC_MARGIN - font.widthOfTextAtSize(marker, TOC_FONT),
      y,
      size: TOC_FONT,
      font,
      color: rowColor,
    });
    y -= 5;
    drawRule(page, y);
    y -= 14;
  }
  return doc;
}

export async function buildNumberedBundlePdf(sections: BundleSection[], labels: BundleLabels): Promise<Blob> {
  const counts: number[] = [];
  const loaded: Array<PDFDocument | null> = [];
  for (const section of sections) {
    const count = await pageCountOf(section.pdf);
    counts.push(count);
    if (!section.pdf || count < 1) {
      loaded.push(null);
      continue;
    }
    try {
      loaded.push(await PDFDocument.load(await section.pdf.arrayBuffer(), { ignoreEncryption: true }));
    } catch (error) {
      console.warn("[bundle] skipped a section PDF that could not be opened", error);
      counts[counts.length - 1] = 0;
      loaded.push(null);
    }
  }

  const rowsFor = (starts: Array<number | null>) => sections.map((section, index) => ({
    title: section.title,
    priority: section.priority,
    startPage: starts[index],
    pageCount: counts[index],
  }));
  let tocPages = 1;
  let starts = bundleStartPages(tocPages, counts);
  let contents = await renderContents(rowsFor(starts), labels);
  for (let pass = 0; pass < 3 && contents.getPageCount() !== tocPages; pass += 1) {
    tocPages = contents.getPageCount();
    starts = bundleStartPages(tocPages, counts);
    contents = await renderContents(rowsFor(starts), labels);
  }

  const output = await PDFDocument.create();
  const tocCopied = await output.copyPages(contents, contents.getPageIndices());
  tocCopied.forEach((page) => output.addPage(page));
  for (let index = 0; index < loaded.length; index += 1) {
    const source = loaded[index];
    if (!source) {
      continue;
    }
    try {
      const pages = await output.copyPages(source, source.getPageIndices());
      pages.forEach((page) => output.addPage(page));
    } catch (error) {
      console.warn("[bundle] copying a section failed, drawing its pages", error);
      const blob = sections[index]?.pdf;
      if (!blob || !(await drawPdfPart(output, blob))) {
        console.warn("[bundle] skipped a section while assembling the PDF", error);
      }
    }
  }
  return pdfBytesBlob(await output.save());
}
